import { isWorldOpen, OPEN_WORLDS, problemTypeLabel } from '../models/world';
import { escapeHtml } from './puzzleScreen';

export interface EndlessSetup {
  readonly worldIndex: number;
  readonly difficulty: number;
}

const SETUP_KEY = 'graphquest.endless.v1';
const DEFAULT_SETUP: EndlessSetup = { worldIndex: 1, difficulty: 5 };
export const MIN_DIFFICULTY = 1;
export const MAX_DIFFICULTY = 10;

const RANKS: readonly (readonly [number, string])[] = [
  [2, 'Courier'],
  [4, 'Runner'],
  [6, 'Wirerunner'],
  [8, 'Veteran'],
  [10, 'Ghost'],
];

export function rankFor(difficulty: number): string {
  return RANKS.find(([upTo]) => difficulty <= upTo)?.[1] ?? 'Ghost';
}

export function clampDifficulty(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SETUP.difficulty;
  return Math.min(MAX_DIFFICULTY, Math.max(MIN_DIFFICULTY, Math.round(value)));
}

export function loadEndlessSetup(): EndlessSetup {
  try {
    const raw = localStorage.getItem(SETUP_KEY);
    if (!raw) return DEFAULT_SETUP;
    const parsed = JSON.parse(raw) as Partial<EndlessSetup>;
    const worldIndex = Number(parsed.worldIndex);
    return {
      worldIndex: isWorldOpen(worldIndex) ? worldIndex : DEFAULT_SETUP.worldIndex,
      difficulty: clampDifficulty(Number(parsed.difficulty)),
    };
  } catch {
    return DEFAULT_SETUP;
  }
}

export function saveEndlessSetup(setup: EndlessSetup): void {
  try {
    localStorage.setItem(SETUP_KEY, JSON.stringify(setup));
  } catch {}
}

export function newSeed(): number {
  return Math.floor(Math.random() * 1_000_000);
}

export function endlessHash(setup: EndlessSetup, seed: number): string {
  return `#/endless/w${setup.worldIndex}/d${setup.difficulty}/${seed}`;
}

export function endlessSetupMarkup(setup: EndlessSetup): string {
  const fronts = OPEN_WORLDS.map(
    (world) => `
      <label class="setup__front${world.id === setup.worldIndex ? ' is-selected' : ''}">
        <input type="radio" name="world" value="${world.id}" ${world.id === setup.worldIndex ? 'checked' : ''} />
        <span class="setup__badge">${world.id}</span>
        <span class="setup__label">
          <b>${escapeHtml(world.name)}</b>
          <i>${escapeHtml(problemTypeLabel(world.problemType))}</i>
        </span>
      </label>
    `,
  ).join('');

  return `
    <div class="screen screen--brief">
      <header class="sub-header">
        <button class="btn btn--ghost btn--icon" data-action="back" aria-label="Back to the map">←</button>
        <div>
          <h1>Endless Runs</h1>
          <p>Generated districts</p>
        </div>
      </header>
      <p class="world-brief">
        Gearfall never sleeps. Pick a front and a difficulty and the city lays out a fresh district
        for every run.
      </p>
      <form class="setup" data-role="setup">
        <fieldset class="setup__group">
          <legend>Front</legend>
          <div class="setup__fronts">${fronts}</div>
        </fieldset>
        <fieldset class="setup__group">
          <legend>Difficulty</legend>
          <div class="setup__difficulty">
            <input type="range" name="difficulty" min="${MIN_DIFFICULTY}" max="${MAX_DIFFICULTY}" step="1"
                   value="${setup.difficulty}" aria-describedby="difficulty-rank" />
            <output id="difficulty-rank" data-role="rank">
              <b>${setup.difficulty}</b> <i>${escapeHtml(rankFor(setup.difficulty))}</i>
            </output>
          </div>
        </fieldset>
        <div class="brief__actions">
          <button type="submit" class="btn btn--primary">Generate run</button>
        </div>
      </form>
    </div>
  `;
}

/** Reads the form back into a setup; the form is the single source of truth while editing. */
export function readEndlessSetup(form: HTMLFormElement): EndlessSetup {
  const data = new FormData(form);
  const worldIndex = Number(data.get('world'));
  return {
    worldIndex: isWorldOpen(worldIndex) ? worldIndex : DEFAULT_SETUP.worldIndex,
    difficulty: clampDifficulty(Number(data.get('difficulty'))),
  };
}
